import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Button, FormField } from '../ui';
import '../../App.css';

/**
 * The season-owned fields shared by the course CREATE bootstrap and the
 * per-season edit form. Content that used to live on the course row (term /
 * capacity / per-session price / enrollment policy / instructor) lives on the
 * SEASON now, so these fields are NOT entity-backed by the `course` meta any
 * more — this fieldset is the non-entity (ui/FormField + primitives) layer for
 * them, reused by both CourseForm (create) and SeasonForm (edit).
 *
 * Strings for the numeric fields (empty string = unset), matching the existing
 * course payload contract; the consuming pages convert to number|null on submit.
 */
export interface SeasonFieldsValue {
  termStart: string; // YYYY-MM-DD, '' = unset
  termEnd: string; // YYYY-MM-DD, '' = unset
  capacity: string; // '' = unlimited
  pricePerSession: string; // '' = not offered
  enrollmentPolicy: 'open' | 'approval';
  instructorId: number | null;
}

export const emptySeasonFields: SeasonFieldsValue = {
  termStart: '',
  termEnd: '',
  capacity: '',
  pricePerSession: '',
  enrollmentPolicy: 'approval',
  instructorId: null,
};

interface InstructorOption {
  _id: number;
  name: string;
}

interface SeasonFieldsetProps {
  value: SeasonFieldsValue;
  onChange: (value: SeasonFieldsValue) => void;
  disabled?: boolean;
  /**
   * When set, offers a "Suggest from schedule" affordance that pre-fills the
   * term start from the course's earliest occurrence (reuses the schedule read).
   */
  courseId?: number | null;
  /** Validation message for the term order (termEnd before termStart). */
  termEndError?: string;
}

/**
 * Renders the six season-owned fields with the project's native input
 * conventions (FormField scaffolding + native date/number/select controls,
 * matching SchemaForm's DefaultFieldWidget). The instructor options are the
 * tenant users (same source the retired `instructorId` fk field used).
 */
const SeasonFieldset: React.FC<SeasonFieldsetProps> = ({
  value,
  onChange,
  disabled = false,
  courseId = null,
  termEndError,
}) => {
  const { t } = useTranslation();
  const [instructors, setInstructors] = useState<InstructorOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.users
      .list()
      .then((r) => {
        if (cancelled) return;
        const data = Array.isArray(r.data) ? r.data : [];
        setInstructors(
          data.map((u: any) => ({ _id: u._id ?? u.id, name: u.name ?? String(u._id ?? u.id) })),
        );
      })
      .catch(() => {
        /* non-critical — the select falls back to "None" only */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = <K extends keyof SeasonFieldsValue>(key: K, v: SeasonFieldsValue[K]) =>
    onChange({ ...value, [key]: v });

  const suggestTermStart = async () => {
    if (courseId == null) return;
    try {
      const { data } = await api.courses.getSchedule(courseId);
      const d = data as {
        templates?: Array<{ firstOccurrence?: string | null }>;
        events?: Array<{ startTime?: string }>;
      };
      const candidates: string[] = [];
      (d.templates ?? []).forEach((tpl) => {
        if (tpl.firstOccurrence) candidates.push(tpl.firstOccurrence);
      });
      (d.events ?? []).forEach((ev) => {
        if (ev.startTime) candidates.push(ev.startTime);
      });
      const dates = candidates
        .map((c) => c.slice(0, 10))
        .filter((c) => /^\d{4}-\d{2}-\d{2}$/.test(c))
        .sort();
      if (dates.length > 0) set('termStart', dates[0]);
    } catch {
      /* non-critical — leave the field untouched */
    }
  };

  return (
    <>
      <div className="schema-form__grid--2">
        <FormField htmlFor="season-termStart" label={t('courses.termStart')}>
          <input
            type="date"
            id="season-termStart"
            value={value.termStart}
            disabled={disabled}
            onChange={(e) => set('termStart', e.target.value)}
          />
          <small className="form-help-text">{t('courses.termStartHelp')}</small>
          {courseId != null && (
            <div style={{ marginTop: '0.5rem' }}>
              <Button type="button" variant="secondary" size="sm" onClick={suggestTermStart} disabled={disabled}>
                {t('courses.suggestFromSchedule')}
              </Button>
            </div>
          )}
        </FormField>

        <FormField htmlFor="season-termEnd" label={t('courses.termEnd')} error={termEndError}>
          <input
            type="date"
            id="season-termEnd"
            value={value.termEnd}
            disabled={disabled}
            onChange={(e) => set('termEnd', e.target.value)}
          />
        </FormField>
      </div>

      <div className="schema-form__grid--2">
        <FormField htmlFor="season-capacity" label={t('courses.capacity')}>
          <input
            type="number"
            id="season-capacity"
            min={0}
            value={value.capacity}
            disabled={disabled}
            onChange={(e) => set('capacity', e.target.value)}
          />
        </FormField>

        <FormField htmlFor="season-pricePerSession" label={t('courses.pricePerSession')}>
          <input
            type="number"
            id="season-pricePerSession"
            min={0}
            step="any"
            value={value.pricePerSession}
            disabled={disabled}
            onChange={(e) => set('pricePerSession', e.target.value)}
          />
          <small className="form-help-text">{t('courses.pricePerSessionHelp')}</small>
        </FormField>
      </div>

      <div className="schema-form__grid--2">
        <FormField htmlFor="season-enrollmentPolicy" label={t('courses.enrollmentPolicy')}>
          <select
            id="season-enrollmentPolicy"
            value={value.enrollmentPolicy}
            disabled={disabled}
            onChange={(e) => set('enrollmentPolicy', e.target.value === 'open' ? 'open' : 'approval')}
          >
            <option value="approval">{t('courses.enrollmentPolicyApproval')}</option>
            <option value="open">{t('courses.enrollmentPolicyOpen')}</option>
          </select>
        </FormField>

        <FormField htmlFor="season-instructor" label={t('courses.instructor')}>
          <select
            id="season-instructor"
            value={value.instructorId != null ? String(value.instructorId) : ''}
            disabled={disabled}
            onChange={(e) => set('instructorId', e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">{t('common.none')}</option>
            {instructors.map((u) => (
              <option key={String(u._id)} value={String(u._id)}>
                {u.name}
              </option>
            ))}
          </select>
        </FormField>
      </div>
    </>
  );
};

export default SeasonFieldset;
